/* eslint-disable react-hooks/exhaustive-deps */
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  Radio,
  Select,
  Upload,
  message,
} from "antd";
import type { UploadProps, UploadFile } from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useReactiveVar } from "@apollo/client";
import dayjs from "dayjs";
import "./ManualNotificationDetail.css";
import recipientNoValidator from "./utils/recipientNoValidator";
import { data } from "../../../../data/data";
import { RegionTypes, ServiceTypes } from "../../../../types/types";
import MainContentHeader from "../../../../components/layout/mainContent/MainContentHeader";
import ContentSection from "../../../../components/layout/mainContent/ContentSection";
import MainContentFooter from "../../../../components/layout/mainContent/MainContentFooter";
import InputCustom from "../../../../components/custom/InputCustom";
import { __currentUser__ } from "../../../../graphql/policies";
import { useAddNotificationMutation } from "../../../../graphql/operations/add-notification";
import { useUploadFileMutation } from "../../../../graphql/operations/upload-file";
import { AfBlobPath } from "../../../../graphql/operations/@types";
import { localUploadBaseProps } from "../../../../utils/localUploadRequest";
import { useGetNotificationLazyQuery } from "../../../../graphql/operations/get-notification";
import { useGetNotificationButtonLinkListQuery } from "../../../../graphql/operations/get-notification-button-link-list";
import { useGetNotificationServiceTypeListQuery } from "../../../../graphql/operations/get-notification-service-type-list";
import Spinner from "../../../../components/layout/spinner/Spinner";
import { renderMarketValue } from "../../../../utils/renderMarketValue";
import { useGetBlobSasQuery } from "../../../../graphql/operations/get-blob-info";
import { renderAntDMessageConfig } from "../../../../utils/renderAntDMessageConfig";
import imageValidation from "../../../../utils/formValidation/imageValidation";
import httpUrlRule from "../../../../utils/formRules/httpUrlRule";
import requiredRule from "../../../../utils/formRules/requiredRule";
import memberNoLengthValidation from "../../../../utils/formValidation/memberNoLengthValidation";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";

const {
  notificationActionTypes,
} = data;
const graduationActionTypes = [
  {
    id: 1,
    typeName: "None",
    typeValue: "none",
  },
  {
    id: 2,
    typeName: "Graduated collection page",
    typeValue: "Graduated",
  },
];
const { TextArea } = Input;

const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

interface ManualNotificationDetailProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const ManualNotificationDetail = ({
  setSelectedService,
  setSelectSubItem,
}: ManualNotificationDetailProps) => {
  const currentUser = useReactiveVar(__currentUser__);
  const [selectDetail, setSelectDetail] = useState<any>();
  const [selectActionType, setSelectActionType] = useState<
    string | undefined
  >();
  const [uploadedFileName, setUploadedFileName] = useState<any>();
  const [uploadedFile, setUploadedFile] = useState<UploadFile>();
  const [excelFileName, setExcelFileName] = useState<string>("");
  const [selectedExcelName, setSelectedExcelName] = useState<string>("");
  const [excelFile, setExcelFile] = useState<UploadFile>();
  const [testId, setTestId] = useState<string | undefined>("");
  const [memberNoByExcel, setMemberNoByExcel] = useState<string[]>([]);
  const [serviceTypeId, setServiceTypeId] = useState("");
  const [inputType, setInputType] = useState("");
  const [fileList, setFileList] = useState<any>([]);
  const { id }: any = useParams();
  const [settingsForm] = Form.useForm();
  const [addNotification, { loading }] = useAddNotificationMutation();
  const [addTestNotification, { loading: testLoading }] =
    useAddNotificationMutation();
  const [uploadFile] = useUploadFileMutation();
  const navigate = useNavigate();
  const [getNotification, { loading: fetchLoading, error }] =
    useGetNotificationLazyQuery({
      variables: { id: parseInt(id) },
      fetchPolicy: "no-cache",
    });
  const { data: blobData, loading: blobLoading, error: blobError } = useGetBlobSasQuery();
  const messageRef = useRef<any>(null);
  const { t: trans } = useTranslation(["clubApp", "common"]);

  const inputTypeList = [
    { label: "excel", text: "Import from file" },
    { label: "all", text: "All members" },
    { label: "manual", text: "Manual input" },
  ];

  const props: UploadProps = {
    ...localUploadBaseProps,
    beforeUpload: imageValidation(100, "KB"),
    onChange: (info) => {
      if (info.fileList.length === 0) {
        setUploadedFileName(undefined);
        setUploadedFile(undefined);
        return;
      }
      setUploadedFileName(info.file.name);
      setUploadedFile(info.file);
    },
  };

  const handleDownloadExample = () => {
    const data = [
      ["memberNo"],
      ["S000001"],
      ["S000002"],
      ["S000003"],
      ["S000004"],
      ["S000005"],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "example.xlsx";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleBeforeUpload = (file: File) => {
    const isExcel =
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.name.endsWith(".xlsx");

    if (!isExcel) {
      message.error("Only .xlsx files are allowed");
      return Upload.LIST_IGNORE;
    }

    if (file.size > 2 * 1024 * 1024) {
      message.error("File must be smaller than 2MB!");
      return Upload.LIST_IGNORE;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const json = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
      }) as (string | number)[][];

      if (!json || json.length === 0 || json[0][0] !== "memberNo") {
        message.error("Invalid format: first column must be 'memberNo'");

        setFileList([]);
        settingsForm.setFieldValue("excel", undefined);

        return Upload.LIST_IGNORE;
      }

      const memberIds = json
        .slice(1)
        .map((row) => row[0] != null ? String(row[0]).trim() : row[0])
        .filter(Boolean) as string[];

      const marketValue = settingsForm.getFieldValue("market");
      const market = RegionTypes[marketValue as keyof typeof RegionTypes];

      for (var i = 0; i < memberIds.length; i++) {
        if (!memberNoLengthValidation(market, memberIds[i])) {
          message.error("Please enter the member number in the correct format.");
          setFileList([]);
          settingsForm.setFieldValue("excel", undefined);

          return Upload.LIST_IGNORE;
        }
      }

      setMemberNoByExcel(memberIds);
    };

    reader.readAsArrayBuffer(file);
    setFileList([file]);
  };

  const uploadProps: UploadProps = {
    ...localUploadBaseProps,
    beforeUpload: handleBeforeUpload,
    onChange: (info) => {
      if (info.fileList.length === 0) {
        setExcelFileName("");
        setExcelFile(undefined);
        return;
      }
      setExcelFileName(info.file.name);
      setExcelFile(info.file);
    },
    fileList,
    onRemove: () => {
      setFileList([]);
    },
  };

  const handleSubmit = (values: any) => {
    let memberIds;
    if (values.members) {
      memberIds = [{ memberNo: values.members }];
    }
    const formData = {
      actionButton: values.actionButton,
      serviceTypeId: values.serviceTypeId,
      title: memberIds ? `[TEST]${values.title}` : values.title,
      templateMessage: values.templateMessage,
      creator: currentUser?.userName,
      scheduleTime: values.scheduleTime
        ? dayjs(values.scheduleTime).toISOString()
        : undefined,
      buttonLinksTo: values.buttonLinksTo,
      imageFileName: uploadedFileName ? uploadedFileName : undefined,
      notificationId: id ? parseInt(id) : null,
      market: values.market
        ? values.market
        : currentUser &&
        currentUser.afUserMarkets &&
        renderMarketValue(currentUser.afUserMarkets[0]?.market),
      buttonText: values.buttonText,
    };

    const members = inputType === "excel"
      ? memberNoByExcel.map((v: any) => {
        return { memberNo: v.trim() };
      })
      : inputType === "manual"
        ? values.recipientNo.split(",").map((v: any) => {
          return { memberNo: v.trim() };
        })
        : null;

    if (inputType === "excel") {
      const marketValue = settingsForm.getFieldValue("market");
      const market = RegionTypes[marketValue as keyof typeof RegionTypes];

      for (var i = 0; i < members.length; i++) {
        if (!memberNoLengthValidation(market, members[i].memberNo)) {
          message.error("Please enter the member number in the correct format.");
          setFileList([]);
          settingsForm.setFieldValue("excel", undefined);

          return;
        }
      }
    }

    if (uploadedFile) {
      uploadFile({
        variables: {
          blobs: [
            {
              file: uploadedFile?.originFileObj,
              path: AfBlobPath.NotificationImage,
            },
          ],
        },
      }).catch((e) => console.log(e));
    }

    if (excelFile) {
      uploadFile({
        variables: {
          blobs: [
            {
              file: excelFile?.originFileObj,
              path: AfBlobPath.AppPushNotificationExcelFiles,
            },
          ],
        },
      }).catch((e) => console.log(e));
    }
    if (memberIds) {
      if (id) {
      } else {
        addTestNotification({
          variables: {
            notification: formData,
            members: memberIds,
            isAllMembers: false,
            isTestNotification: true,
            excelFileName: excelFileName,
            recipientSourceType: inputType,
          },
        })
          .then(() => {
            settingsForm.setFieldValue("members", "");
            setTestId("");
            if (values.scheduleTime) {
              message.success(
                renderAntDMessageConfig(
                  "Scheduled Notification has been submitted."
                )
              );
            } else {
              message.success(
                renderAntDMessageConfig(
                  "Notification has been submitted. This can sometimes take a few minutes to arrive."
                )
              );
            }
          })
          .catch((e) => console.log(e));
      }
    } else {
      if (id) {
      } else {
        addNotification({
          variables: {
            notification: formData,
            members: members,
            isAllMembers: inputType === "all",
            isTestNotification: false,
            excelFileName: excelFileName,
            recipientSourceType: inputType,
          },
        })
          .then(() => {
            if (values.scheduleTime) {
              message.success(
                renderAntDMessageConfig(
                  "Scheduled Notification has been submitted."
                )
              );
            } else {
              message.success(
                renderAntDMessageConfig(
                  "Notification has been submitted. This can sometimes take a few minutes to arrive."
                )
              );
            }
            navigate("/club-app/push-notification");
          })
          .catch((e) => console.log(e));
      }
    }
  };

  const handleRePush = () => {
    const formData: any = {
      actionButton: selectDetail.actionButton,
      serviceTypeId: selectDetail.afNotificationServiceTypeId,
      title: selectDetail.title,
      templateMessage: selectDetail.templateMessage,
      creator: selectDetail.creator,
      scheduleTime: null,
      buttonLinksTo: selectDetail.buttonLinksTo,
      imageFileName: selectDetail.imageFileName,
      notificationId: null,
      market: renderMarketValue(selectDetail.market),
      buttonText: selectDetail.buttonText,
    };
    addNotification({
      variables: {
        notification: formData,
        members:
          selectDetail.recipientSourceType === "all"
            ? null
            : selectDetail.afNotificationMemberLogs.map((i: any) => {
              return { memberNo: i.memberNumber };
            }),
        isAllMembers: selectDetail.recipientSourceType === "all",
        recipientSourceType: selectDetail.recipientSourceType,
        excelFileName: selectedExcelName,
      },
    })
      .then(() => {
        message.success(
          "Notification has been submitted. This can sometimes take a few minutes to arrive."
        );
        navigate("/club-app/push-notification");
      })
      .catch((e) => console.log(e));
  };

  const renderActionType = (type: string) => {
    switch (type) {
      case "none":
        return "None";
      case "AppScreen":
        return "Club app screen";
      case "AppEmbedURL":
        return "Embed URL";
      case "OpenBrowser":
        return "Open browser";
      default:
        break;
    }
  };

  useEffect(() => {
    setSelectedService(ServiceTypes.CLUB_APP);
    setSelectSubItem("Push Notification");
  }, []);

  useEffect(() => {
    if (selectDetail && selectDetail.excelFileName) {
      const queryIndex = selectDetail.excelFileName.indexOf("?");
      const lastSlashBeforeQuery = selectDetail.excelFileName.lastIndexOf(
        "/",
        queryIndex
      );

      const fileName = selectDetail.excelFileName.slice(
        lastSlashBeforeQuery + 1,
        queryIndex
      );
      setSelectedExcelName(fileName || "");
    }
  }, [selectDetail]);

  useEffect(() => {
    if (id) {
      getNotification()
        .then((r) => {
          if (r.data?.wf_adminFirst_afNotification?.edges) {
            setSelectDetail(
              r.data?.wf_adminFirst_afNotification?.edges[0].node
            );
          }
        })
        .catch((e) => console.log(e));
    }
  }, [id]);

  useEffect(() => {
    settingsForm.setFieldValue("actionButton", "none");
    settingsForm.setFieldValue("market", "HONG_KONG");
    if (
      currentUser &&
      currentUser.afUserMarkets &&
      currentUser.afUserMarkets.length === 1
    ) {
      settingsForm.setFieldsValue({
        market: renderMarketValue(currentUser.afUserMarkets[0]?.market),
      });
    }
  }, [currentUser]);

  const watchedMarket = Form.useWatch("market", settingsForm);
  const marketAbbr =
    selectDetail?.market ??
    RegionTypes[watchedMarket as keyof typeof RegionTypes] ??
    (currentUser?.afUserMarkets?.length === 1
      ? currentUser?.afUserMarkets[0]?.market
      : undefined);

  const { data: buttonLinkData } =
    useGetNotificationButtonLinkListQuery({
      variables: { market: marketAbbr },
    });
  const buttonLinkList = buttonLinkData?.pushNotificationButtonLinkList ?? [];

  const { data: serviceTypeData } =
    useGetNotificationServiceTypeListQuery({
      variables: { market: marketAbbr },
    });
  const serviceTypeList = serviceTypeData?.pushNotificationServiceTypeList ?? [];

  if (error || blobError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (fetchLoading || blobLoading) {
    return <Spinner />;
  }

  return (
    <div style={{ position: "relative", paddingBottom: 48 }}>
      <MainContentHeader
        title={
          selectDetail &&
            (selectDetail.scheduleTime <= dayjs().toISOString() ||
              !selectDetail.scheduleTime)
            ? "View notification detail"
            : "Notification detail"
        }
      />
      <ContentSection title="" showTitle={false}>
        {selectDetail ? (
          <Form
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            className="none-edit"
          >
            {currentUser &&
              currentUser.afUserMarkets &&
              currentUser.afUserMarkets.length > 1 && (
                <Form.Item label={trans("common:common.market")}>
                  <p>{selectDetail.market}</p>
                </Form.Item>
              )}
            <Form.Item label={trans("clubApp:notification.serviceType")}>
              <p>
                {serviceTypeList.find(
                  (item) =>
                    item.serviceTypeId === selectDetail.afNotificationServiceTypeId
                )?.serviceTypeName}
              </p>
            </Form.Item>
            <Form.Item label={trans("clubApp:notification.addRecipientsBy")}>
              <p>
                {
                  inputTypeList.find(
                    (i) => i.label === selectDetail.recipientSourceType
                  )?.text
                }
              </p>
            </Form.Item>
            {selectDetail.recipientSourceType === "manual" && (
              <Form.Item label={trans("clubApp:notification.recipientsMemberNo")}>
                {selectDetail.afNotificationMemberLogs.map(
                  (item: any, index: number) => {
                    return (
                      <span className="memberNo">
                        {index === 0 ? "" : ", "}
                        {item.memberNumber}
                      </span>
                    );
                  }
                )}
              </Form.Item>
            )}
            {selectDetail.recipientSourceType === "excel" && (
              <Form.Item label="Upload Excel file (.xlsx)">
                <a
                  className="underline cursor-pointer"
                  href={selectDetail.excelFileName}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selectedExcelName}
                </a>
              </Form.Item>
            )}
            <Form.Item label={trans("common:common.title")}>
              <p>{selectDetail.title}</p>
            </Form.Item>
            <Form.Item label={trans("clubApp:notification.message")}>
              <p
                ref={messageRef}
                style={{
                  width: 480,
                  lineHeight: "22px",
                  paddingTop: messageRef?.current?.clientHeight > 30 ? 5 : 0,
                }}
              >
                {selectDetail.templateMessage}
              </p>
            </Form.Item>
            {selectDetail.imageFileName && (
              <Form.Item label="Uploaded image">
                <div style={{ width: "100px" }}>
                  <img
                    src={`${blobData?.afBlobContainerSignature[3].uri}/${selectDetail.imageFileName}${blobData?.afBlobContainerSignature[3].token}`}
                    alt=""
                    style={{ maxWidth: "100%" }}
                  />
                </div>
              </Form.Item>
            )}
            <Form.Item label={trans("clubApp:notification.actionButton")}>
              <p>{renderActionType(selectDetail.actionButton)}</p>
            </Form.Item>
            {selectDetail.buttonLinksTo && (
              <Form.Item label={trans("clubApp:notification.buttonLinksTo")}>
                <p>{selectDetail.buttonLinksTo}</p>
              </Form.Item>
            )}
            {selectDetail.buttonText && (
              <Form.Item label={trans("clubApp:notification.buttonText")}>
                <p>{selectDetail.buttonText}</p>
              </Form.Item>
            )}
            <Form.Item label={trans("clubApp:notification.pushDateAndTime")}>
              <p>
                {selectDetail.scheduleTime
                  ? dayjs(selectDetail.scheduleTime).format("YYYY-MM-DD HH:mm")
                  : dayjs(selectDetail.createdTime).format("YYYY-MM-DD HH:mm")}
              </p>
            </Form.Item>
            <MainContentFooter
              isLoading={loading}
              backTo="/club-app/push-notification"
              isOneButton
              handleClick={handleRePush}
            />
          </Form>
        ) : (
          <Form
            form={settingsForm}
            name="settings"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 12 }}
            onValuesChange={(changedValues) => {
              if (changedValues.serviceTypeId) {
                setServiceTypeId(changedValues.serviceTypeId);
              }
            }}
            onFinish={handleSubmit}
          >
            {currentUser &&
              currentUser.afUserMarkets &&
              currentUser.afUserMarkets.length > 1 && (
                <Form.Item
                  label={trans("common:common.market")}
                  name="market"
                  rules={[
                    { required: true, message: "This field is required!" },
                  ]}
                >
                  <Radio.Group
                    options={[
                      { label: "HK", value: "HONG_KONG" },
                      { label: "KR", value: "KOREA" },
                      { label: "JP", value: "JAPAN" },
                      { label: "TW", value: "TAIWAN" },
                    ]}
                    optionType="button"
                  />
                </Form.Item>
              )}
            <Form.Item
              label={trans("clubApp:notification.serviceType")}
              name="serviceTypeId"
              rules={[{ required: true, message: "This field is required!" }]}
            >
              <Select>
                {serviceTypeList.map((item) => (
                  <Select.Option key={item.serviceTypeId} value={item.serviceTypeId}>
                    {item.serviceTypeName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label={trans("clubApp:notification.addRecipientsBy")}
              rules={[requiredRule]}
              name="input-type"
            >
              <Radio.Group
                options={[
                  { label: trans("clubApp:notification.allRecipients"), value: "all" },
                  { label: trans("clubApp:notification.manualInput"), value: "manual" },
                  { label: trans("clubApp:notification.importFromFile"), value: "excel" },
                ]}
                optionType="button"
                onChange={(e) => setInputType(e.target.value)}
              />
            </Form.Item>
            {inputType === "manual" && (
              <Form.Item
                label={trans("clubApp:notification.recipientsMemberNo")}
                name="recipientNo"
                rules={[
                  requiredRule,
                  {
                    validator: (_, value) => {
                      const marketValue = settingsForm.getFieldValue("market");
                      const market = RegionTypes[marketValue as keyof typeof RegionTypes];
                      return recipientNoValidator(market)(_, value);
                    },
                  },
                ]}
              >
                <InputCustom placeholder="Enter the member number(s), separated by commas." />
              </Form.Item>
            )}
            {inputType === "excel" && (
              <Form.Item
                label={trans("clubApp:notification.uploadFile", { fileName: 'Excel', format: '.xlsx' })}
                name="excel"
                rules={[requiredRule]}
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) return e;
                  return e?.fileList;
                }}
              >
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
              </Form.Item>
            )}
            {inputType === "excel" && (
              <Form.Item
                wrapperCol={{ offset: 8, span: 12 }}
                style={{ marginTop: "-24px" }}
              >
                <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                  Please follow the specified format:&nbsp;
                </span>
                <span
                  className="cursor-pointer link"
                  onClick={handleDownloadExample}
                >
                  Download reference file
                </span>
              </Form.Item>
            )}
            <Form.Item label={trans("common:common.title")} name="title">
              <InputCustom placeholder="Title shown on the screen" />
            </Form.Item>
            <Form.Item
              label={trans("clubApp:notification.message")}
              name="templateMessage"
              rules={[requiredRule]}
            >
              <TextArea
                showCount
                maxLength={300}
                style={{ height: 54 }}
                placeholder="Autosize height based on content lines
"
              />
            </Form.Item>
            <Form.Item
              label={trans("clubApp:notification.imageOptional")}
              valuePropName="fileList"
              getValueFromEvent={normFile}
            >
              <Upload
                {...props}
                listType="picture-card"
                maxCount={1}
              >
                <div style={{ position: "relative" }}>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>{trans("common:button.upload")}</div>
                  <ul
                    style={{
                      position: "absolute",
                      left: "120px",
                      top: "-24px",
                      width: 300,
                      textAlign: "left",
                      color: "rgba(0, 0, 0, 0.45)",
                      listStyle: "disc",
                    }}
                  >
                    <li>{trans("common:tips.maxFileSize", { fileSize: 100, unit: 'KB' })}</li>
                    <li>{trans("common:tips.recommendedWidth", { fileSize: 600, unit: 'px' })}</li>
                    <li>
                      {trans("common:tips.getMonthlyFaceCallBannerFrom")}&nbsp;
                      <a
                        href="https://wfeinnovation.atlassian.net/wiki/x/RwAKWQ"
                        className="link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        https://wfeinnovation.atlassian.net/wiki/x/RwAKWQ
                      </a>
                    </li>
                  </ul>
                </div>
              </Upload>
            </Form.Item>
            {selectDetail && selectDetail.imageFileName && (
              <Form.Item label="Uploaded image">
                <div style={{ width: "100px" }}>
                  <img
                    src={`${blobData?.afBlobContainerSignature[3].uri}/${selectDetail.imageFileName}${blobData?.afBlobContainerSignature[3].token}`}
                    alt=""
                    style={{ maxWidth: "100%" }}
                  />
                </div>
              </Form.Item>
            )}
            <Form.Item
              label={trans("clubApp:notification.actionButton")}
              name="actionButton"
              rules={[{ required: true, message: "This field is required!" }]}
            >
              <Radio.Group
                options={notificationActionTypes.map((type) => ({
                  label: trans(`clubApp:notification.${type.typeValue}`, { defaultValue: type.typeName }),
                  value: type.typeValue,
                }))}
                optionType="button"
                onChange={(e) => {
                  setSelectActionType(e.target.value);
                  settingsForm.setFieldsValue({
                    buttonLinksTo: "",
                    buttonText: "",
                  });
                }}
              />
            </Form.Item>
            {selectActionType && selectActionType !== "none" && (
              <>
                {selectActionType === "AppScreen" ? (
                  <Form.Item label={trans("clubApp:notification.buttonLinksTo")} name="buttonLinksTo">
                    <Select placeholder="- Select -">
                      {buttonLinkList.map((item) => (
                        <Select.Option key={item.buttonLinkId} value={item.buttonLinkId}>
                          {item.buttonLinkName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                ) : (
                  <Form.Item label={trans("clubApp:notification.buttonLinksTo")} name="buttonLinksTo" rules={[httpUrlRule(false)]}>
                    <InputCustom placeholder="https://www.worldfamilyenglish.com" />
                  </Form.Item>
                )}
                <Form.Item
                  label={trans("clubApp:notification.buttonText")}
                  name="buttonText"
                  rules={[requiredRule]}
                >
                  <InputCustom />
                </Form.Item>
              </>
            )}
            <Form.Item label={trans("clubApp:notification.pushDateAndTime")} name="scheduleTime">
              <DatePicker
                showTime
                style={{ width: "100%" }}
                placeholder="Select date and time"
                format="YYYY-MM-DD HH:mm"
              />
            </Form.Item>
            <Form.Item
              wrapperCol={{ offset: 8, span: 12 }}
              style={{ marginTop: "-24px" }}
            >
              <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>
                {trans("clubApp:notification.sendImmediatelyWhenNotSelectDateAndTime")}
              </span>
            </Form.Item>
            <Form.Item wrapperCol={{ offset: 6, span: 14 }}>
              <Divider />
            </Form.Item>
            <Form.Item
              label={trans("clubApp:notification.testMemberNo")}
              name="members"
              rules={[
                {
                  validator: (rule: any, value: string) => {
                    if (!value) return Promise.resolve();

                    const isValid = /^[a-zA-Z0-9]{1,8}$/.test(value);

                    return isValid
                      ? Promise.resolve()
                      : Promise.reject(
                        "Please enter the member number in the correct format."
                      );
                  },
                },
              ]}
            >
              <Input
                placeholder="Enter the member number for testing"
                onChange={(e) => setTestId(e.target.value)}
              />
            </Form.Item>
            <Form.Item
              wrapperCol={{ offset: 8, span: 12 }}
              style={{ marginTop: "-24px" }}
            >
              <Button
                htmlType="submit"
                type="primary"
                style={{ marginTop: 24 }}
                loading={testLoading}
                disabled={!testId}
              >
                {trans("common:button.submitToTest")}
              </Button>
            </Form.Item>
            {!id && (
              <MainContentFooter
                isLoading={loading}
                disabled={testId !== ""}
                testId={testId}
                backTo="/club-app/push-notification"
              />
            )}
          </Form>
        )}
      </ContentSection>
    </div>
  );
};

export default ManualNotificationDetail;
